import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

/**
 *
 * @param {string} type
 * @param {Array<RealTimeData<T>>} data
 * Usage:
 *  take timeseries data and filter by specific period.
 * Periods:
 *  0.5hr => 30 * 1 step per min
 *  1hr => 30 * 1 step per 2mins
 *  6hrs => 6 * 2 * 1 step per 30mins
 *  12hrs => 12 * 2 * 1 step per 30mins
 *  24hrs => 24 * 1 step per 60mins
 */

const periodConfigs = {
  30: {
    text: "0.5hr",
    step: 30,
    duration: dayjs.duration(1, "minute")
  },
  60: {
    text: "1hr",
    step: 30,
    duration: dayjs.duration(2, "minutes")
  },
  360: {
    text: "6hrs",
    step: 12,
    duration: dayjs.duration(30, "minutes")
  },
  720: {
    text: "12hrs",
    step: 24,
    duration: dayjs.duration(30, "minutes")
  },
  1440: {
    text: "24hrs",
    step: 24,
    duration: dayjs.duration(60, "minutes")
  }
};
const dateFormat = "YYYY-MM-DD HH:mm";

export const timestampCherryPick = (timeInterval, data) => {
  if (!data) {
    return 0;
  }
  const timePeriod = data.length / timeInterval;
  const period = dayjs.duration(timePeriod, "minutes");
  const filteredData = [];
  let current = null;

  current = dayjs(data[0]["created_at"], dateFormat);
  for (let d of data) {
    if (d["created_at"] === current.format(dateFormat)) {
      filteredData.push(d);
      current = current.subtract(period);
    }
  }

  return filteredData;
};

const pickInOneHour = (dateInhour, data) => {
  const filteredData = [];
  const targetHour = dayjs.utc(dateInhour).format("YYYY-MM-DD HH");
  for (let d of data) {
    if (dayjs(d["created_at"]).format("YYYY-MM-DD HH") === targetHour) {
      filteredData.push(d);
    }
  }
  return filteredData;
};

export const timestampCherryPickInOneHour = (
  dateInhour,
  data,
  numOfPartition = 60
) => {
  if (!data) {
    return 0;
  }
  const filteredData = pickInOneHour(dateInhour, data);
  const filteredDataLen = filteredData.length;
  // 如果不足numOfPartition则补全上一小时的数据
  if (filteredDataLen > 0 && filteredDataLen < numOfPartition) {
    const lastItem = filteredData[filteredDataLen - 1];
    const lastDate = dayjs(lastItem["created_at"]);
    const prevHourData = pickInOneHour(dateInhour.subtract(1, "hours"), data);
    [].push.apply(
      filteredData,
      Array.from({ length: numOfPartition - filteredDataLen }).map((_, i) => {
        return {
          // prettier-ignore
          created_at: lastDate.subtract(i + 1, "minutes").format("YYYY-MM-DD HH:mm"),
          name: lastItem.name || "",
          unit: lastItem.unit || "",
          value: prevHourData[i]?.value ?? "0"
        };
      })
    );
  }

  return filteredData;
};

export const cpuTemperatureTimestampCherryPick = (type, data) => {
  if (!data) {
    return 0;
  }

  const normalize = w =>
    dayjs(w.replace(/\.0{6}/, ""))
      .utc()
      .format(dateFormat);

  const period = periodConfigs[type];
  const filteredData = {
    label: [],
    dataSets: (data.dataSets ?? []).map(ds => ({
      label: ds.label,
      data: []
    }))
  };

  if (!data.label.length) {
    return filteredData;
  }

  let current = null;

  current = dayjs(normalize(data.label[data.label.length - 1]), dateFormat);
  for (let idx = data.label.length - 1; idx >= 0; idx--) {
    if (normalize(data.label[idx]) === current.format(dateFormat)) {
      filteredData.label.unshift(data.label[idx]);
      filteredData.dataSets.forEach((ds, coreIdx) => {
        ds.data.unshift(data.dataSets[coreIdx].data[idx]);
      });
      current = current.subtract(period.duration);
    }
  }

  return filteredData;
};

const pickTempratureTimestampCherryPickInOneHour = (
  filteredData,
  data,
  dateInHour,
  limit
) => {
  const dateFormat = "YYYY-MM-DD HH";

  const normalize = w =>
    dayjs(w.replace(/\.0{6}/, ""))
      .utc()
      .format(dateFormat);

  if (!data.label?.length) {
    return filteredData;
  }

  let count = 0;
  const targetHour = dayjs.utc(dateInHour).format(dateFormat);
  for (let idx = data.label.length - 1; idx >= 0; idx--) {
    if (normalize(data.label[idx]) === targetHour) {
      if (!limit || count < limit) {
        filteredData.label.unshift(data.label[idx]);
        filteredData.dataSets.forEach((ds, coreIdx) => {
          ds.data.unshift(data.dataSets[coreIdx].data[idx]);
        });
      }
      count++;
    }
  }

  return filteredData;
};

export const cpuTemperatureTimestampCherryPickInOneHour = (
  type,
  data,
  dateInHour,
  numOfPartition = 60
) => {
  if (!data) {
    return 0;
  }

  const filteredData = {
    label: [],
    dataSets: (data.dataSets ?? []).map(ds => ({
      label: ds.label,
      data: []
    }))
  };

  pickTempratureTimestampCherryPickInOneHour(
    filteredData,
    data,
    dateInHour,
    numOfPartition
  );

  let filteredDataLen = filteredData.label.length;
  // 如果不足numOfPartition则补全上一小时的数据
  if (filteredDataLen < numOfPartition) {
    pickTempratureTimestampCherryPickInOneHour(
      filteredData,
      data,
      dateInHour.subtract(1, "hours"),
      numOfPartition - filteredDataLen
    );

    filteredDataLen = filteredData.label.length;
    // 如果仍然不足numOfPartition则补零
    if (filteredDataLen < numOfPartition) {
      const lastItem = filteredData.label[filteredDataLen - 1];
      const lastDate = dayjs.utc(lastItem);
      for (let i = 0; i < numOfPartition - filteredDataLen; i++) {
        filteredData.label.unshift(
          lastDate.subtract(i + 1, "minutes").toISOString()
        );
        filteredData.dataSets.forEach(ds => {
          ds.data.unshift("0");
        });
      }
    }
  }

  return filteredData;
};
